/**
 * Keyboard/Mouse input provider - sends raw device states
 */

import type { InputProvider, RawInputState } from "@game/domain";

export class KeyboardMouseInput implements InputProvider {
  private canvas: HTMLCanvasElement;

  // Key state
  private keysPressed = new Set<string>();
  private isShiftPressed = false;

  // Mouse state (raw canvas coordinates)
  private mouseCanvasX = 0;
  private mouseCanvasY = 0;
  private isShooting = false;

  // Event handler references (for cleanup)
  private handlers = {
    keyDown: this.handleKeyDown.bind(this),
    keyUp: this.handleKeyUp.bind(this),
    mouseDown: this.handleMouseDown.bind(this),
    mouseUp: this.handleMouseUp.bind(this),
    mouseMove: this.handleMouseMove.bind(this),
    contextMenu: this.handleContextMenu.bind(this),
  };

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.mouseCanvasX = canvas.width / 2;
    this.mouseCanvasY = canvas.height / 2;
    this.attachEventListeners();
  }

  private attachEventListeners(): void {
    document.addEventListener("keydown", this.handlers.keyDown);
    document.addEventListener("keyup", this.handlers.keyUp);
    this.canvas.addEventListener("mousedown", this.handlers.mouseDown);
    this.canvas.addEventListener("mouseup", this.handlers.mouseUp);
    this.canvas.addEventListener("mousemove", this.handlers.mouseMove);
    this.canvas.addEventListener("contextmenu", this.handlers.contextMenu);
  }

  private handleContextMenu(e: Event): void {
    // Prevent right-click context menu from appearing
    e.preventDefault();
  }

  private handleKeyDown(e: KeyboardEvent): void {
    // Ignore if typing in input field
    if (
      e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLTextAreaElement
    ) {
      return;
    }

    // Track Shift key for strafe mode
    if (e.code === "ShiftLeft" || e.code === "ShiftRight") {
      this.isShiftPressed = true;
      return;
    }

    const key = e.key.toLowerCase();
    this.keysPressed.add(key);
  }

  private handleKeyUp(e: KeyboardEvent): void {
    // Track Shift key for strafe mode
    if (e.code === "ShiftLeft" || e.code === "ShiftRight") {
      this.isShiftPressed = false;
      return;
    }

    const key = e.key.toLowerCase();
    this.keysPressed.delete(key);
  }

  private handleMouseDown(e: MouseEvent): void {
    if (e.button === 0) {
      // Left click - shoot
      this.isShooting = true;
    } else if (e.button === 2) {
      // Right click - deploy mine
      // Import and call handleMineDeployment dynamically to avoid circular dependencies
      import("../mines/mines").then((mines) => {
        mines.handleMineDeployment();
      });
    }
  }

  private handleMouseUp(e: MouseEvent): void {
    if (e.button === 0) {
      this.isShooting = false;
    }
  }

  private handleMouseMove(e: MouseEvent): void {
    const rect = this.canvas.getBoundingClientRect();
    // Store raw canvas coordinates (server will calculate offset)
    this.mouseCanvasX = e.clientX - rect.left;
    this.mouseCanvasY = e.clientY - rect.top;
  }

  update(_deltaTime: number): void {
    // No-op: raw input doesn't need frame updates
    // Kept for interface compatibility
  }

  getInputState(): RawInputState {
    const hasSpace = this.keysPressed.has(" ");
    return {
      keyForward: this.keysPressed.has("w") || this.keysPressed.has("arrowup"),
      keyBack: this.keysPressed.has("s") || this.keysPressed.has("arrowdown"),
      keyLeft: this.keysPressed.has("a") || this.keysPressed.has("arrowleft"),
      keyRight: this.keysPressed.has("d") || this.keysPressed.has("arrowright"),
      keyBoost: hasSpace,
      keyShift: this.isShiftPressed,
      mouseScreenX: this.mouseCanvasX,
      mouseScreenY: this.mouseCanvasY,
      canvasWidth: this.canvas.width,
      canvasHeight: this.canvas.height,
      mouseShoot: this.isShooting,
      timestamp: performance.now(),
    };
  }

  destroy(): void {
    document.removeEventListener("keydown", this.handlers.keyDown);
    document.removeEventListener("keyup", this.handlers.keyUp);
    this.canvas.removeEventListener("mousedown", this.handlers.mouseDown);
    this.canvas.removeEventListener("mouseup", this.handlers.mouseUp);
    this.canvas.removeEventListener("mousemove", this.handlers.mouseMove);
    this.canvas.removeEventListener("contextmenu", this.handlers.contextMenu);
  }
}
